import React, { useState } from 'react';
import { ButtonReact } from 'common/components/button';
import { DropMenuReact } from 'common/components/drop-menu';
import { useTranslation } from 'common/i18n/hooks';
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { EntrySectionUpdates } from 'common/query-builder/query-entry-section/types';
import { getProjectId } from 'common/report/util';
import { SelectTheme } from 'common/components/select/constants';
import { SelectReact } from 'common/components/select';
import { ResourceType } from 'common/report/constants';
import { FilterClause } from 'common/report/insights/models/filter-clause';
import { ToastType } from 'common/components/toast/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { Visibility, VisibilityChangeEvent } from 'common/components/base/stackable';
import { DateRange } from '../date-range-picker/models/date-range';
import { DateRangePickerReact } from '../date-range-picker-new';
import { DisplayContext } from '../date-range-picker/models/types';
import { PropertyFilter } from '../property-filter-menu/models/property-filter';
import styles from './index.module.styl';
import { EventCombinationSetting, EventFilterEntry, EventFilterInfo, RecordingPlayerSource, SortOption, SortOptionTranslationLabels } from './types';
type RecordingPlaylistHeaderProps = {
  source: RecordingPlayerSource;
  selectedSortOption: string;
  updateSortOptionFn: (option: SortOption) => void;
  visibleRecordingsCount: number;
  // Optional props for SR Home
  loadingReplays?: boolean;
  replayCount?: number;
  replayDateRange?: DateRange;
  setDateRangeFn?: (dateRange: DateRange) => void;
  setEventFilterInfoFn?: (eventFilters: EventFilterInfo[]) => void;
  combination?: EventCombinationSetting;
  setCombinationFn?: (combination: EventCombinationSetting) => void;
  filterEntries?: EventFilterEntry[];
  setFilterEntries?: (entries: EventFilterEntry[]) => void;
};
export function PlaylistHeader(props: RecordingPlaylistHeaderProps) {
  var _props$combination, _props$filterEntries;
  const [dateMenuOpened, setDateMenuOpened] = useState<boolean>(false);
  const [eventFilterMenuOpened, setEventFilterMenuOpened] = useState<boolean>(false);
  const [numFiltersActive, setNumFiltersActive] = useState<number>(1);
  const [numEventsSelected, setNumEventsSelected] = useState<number>(1);
  const [currentLogicOp, setCurrentLogicOp] = useState<EventCombinationSetting>((_props$combination = props.combination) !== null && _props$combination !== void 0 ? _props$combination : EventCombinationSetting.OR);
  const [currentEntries, setCurrentEntries] = useState<EventFilterEntry[]>((_props$filterEntries = props.filterEntries) !== null && _props$filterEntries !== void 0 ? _props$filterEntries : []);
  const {
    t,
    print
  } = useTranslation();
  const projectId = getProjectId();
  const resetFilters = () => {
    var _props$filterEntries2;
    setCurrentEntries((_props$filterEntries2 = props.filterEntries) !== null && _props$filterEntries2 !== void 0 ? _props$filterEntries2 : []);
    if (props.combination) {
      setCurrentLogicOp(props.combination);
    }
  };
  return <div className={styles.replaysListHeader} data-sentry-component="PlaylistHeader" data-sentry-source-file="playlist-header.tsx">
      {props.source === RecordingPlayerSource.ReplayHome ? <div className={styles.replaysListHeaderFilter}>
          <ButtonReact disabled={props.loadingReplays} className={styles.replayDateButton} onClick={() => {
        setDateMenuOpened(true);
        setEventFilterMenuOpened(false);
      }} icon='calendar'>
            {props.replayDateRange ? print(props.replayDateRange.getDisplayText()) : t("session-replay.unknown-date")}
          </ButtonReact>
          <DropMenuReact open={dateMenuOpened} disableAutoFocus
      // @ts-ignore this onChange type is conflicting with dom onChange form event
      onChange={(ev: VisibilityChangeEvent) => {
        if (ev.detail.state !== Visibility.Open) {
          setDateMenuOpened(false);
        }
      }}>
            <DateRangePickerReact forceHidePresetMenu displayContext={DisplayContext.FullLength} onApply={ev => {
          if (props.setDateRangeFn) {
            props.setDateRangeFn(ev.detail.dateRange);
            setDateMenuOpened(false);
          }
        }} onCancel={() => setDateMenuOpened(false)} />
          </DropMenuReact>
          <div className={styles.organizationContainer}>
            <DropMenuReact preventStacking={currentEntries !== props.filterEntries} className={styles.filterContainer} open={eventFilterMenuOpened}
        // @ts-ignore this onChange type is conflicting with dom onChange form event
        onChange={(ev: VisibilityChangeEvent) => {
          if (ev.detail.state !== Visibility.Open) {
            setEventFilterMenuOpened(false);
            resetFilters();
          }
        }}>
              <div className={styles.eventFilterContainer}>
                <div className={styles.eventFilterTitle}>{t("session-replay.event-filter")}</div>
                <SelectReact className={styles.combinationFilter} sections={[{
              items: [{
                label: EventCombinationSetting.AND,
                value: EventCombinationSetting.AND
              }, {
                label: EventCombinationSetting.OR,
                value: EventCombinationSetting.OR
              }]
            }]} selectedOption={currentLogicOp} onChange={(ev: any) => {
              setCurrentLogicOp(ev.detail.value as EventCombinationSetting);
            }} />
              </div>
              <QueryEntrySectionReact className={styles.filterBuilder} rowTypes={[ResourceType.Event]} projectId={+projectId} disableCloneEntry minEntries={1} entries={currentEntries} onUpdate={(updates: EntrySectionUpdates<any>) => {
            setCurrentEntries(updates.entries);
          }} />
              <div className={styles.organizationContainer}>
                <ButtonReact className={styles.filterSelectButton} onClick={() => {
              setEventFilterMenuOpened(false);
              resetFilters();
            }}>
                  {t("common.cancel")}
                </ButtonReact>
                <ButtonReact className={styles.filterSelectButton} onClick={() => {
              if (props.setEventFilterInfoFn) {
                let totalNumFilters = 0;
                const events = currentEntries.map((entry): EventFilterInfo => {
                  totalNumFilters += entry.filters.length;
                  return {
                    name: entry.event.value,
                    filters: entry.filters.map((filter: any) => FilterClause.fromPropertyFilter(new PropertyFilter(filter)))
                  };
                });
                totalNumFilters += events.length;
                props.setEventFilterInfoFn(events);
                setNumFiltersActive(totalNumFilters);
                setEventFilterMenuOpened(false);
                setNumEventsSelected(currentEntries.length);
                if (props.setFilterEntries) {
                  props.setFilterEntries(currentEntries);
                }
                if (props.setCombinationFn) {
                  if (currentLogicOp === EventCombinationSetting.AND) {
                    addToast({
                      message: t("session-replay.and-filter-limit-warning"),
                      type: ToastType.INFO
                    });
                  }
                  props.setCombinationFn(currentLogicOp);
                }
              }
              setEventFilterMenuOpened(false);
            }}>
                  {t("common.apply")}
                </ButtonReact>
              </div>
            </DropMenuReact>

            <ButtonReact disabled={props.loadingReplays} className={styles.filterButton} onClick={() => {
          setDateMenuOpened(false);
          setEventFilterMenuOpened(true);
        }} icon='filter-xs'>
              {numFiltersActive > 0 ? t("session-replay.num-filters-set", {
            count: numFiltersActive
          }) : t("query-builder.filter")}
            </ButtonReact>
            <SelectReact disabled={props.loadingReplays} className={styles.sortButton} theme={SelectTheme.Secondary} icon={{
          leftIcon: "sort"
        }} menuWidth={150} sections={[{
          items: Object.keys(SortOption).map(option => ({
            value: option,
            label: print(t(SortOptionTranslationLabels[option]))
          }))
        }]} options={Object.values(SortOption)} onChange={(ev: any) => {
          props.updateSortOptionFn(ev.detail.item.value as SortOption);
        }} selectedOption={print(t(SortOptionTranslationLabels[props.selectedSortOption]))} tooltip={{
          text: t("session-replay.sort-tooltip")
        }} />
          </div>
          <div className={styles.replaysListHeaderSubtitle}>
            {props.loadingReplays ? t("session-replay.loading-replays") :
        // TODO Fix this once we can correctly get the correct count of borrowed events
        props.replayCount && numEventsSelected <= 1 ? t("session-replay.replays-count-subtotal", {
          subtotal: props.visibleRecordingsCount,
          total: props.replayCount.toLocaleString()
        }) : t("session-replay.replays-count", {
          number: props.visibleRecordingsCount.toLocaleString()
        })}
          </div>
        </div> : <div className={styles.replaysListHeaderText}>
          <div className={styles.replaysListHeaderTitle}>{t("session-replay.replays")}</div>
          {!props.loadingReplays && <div className={styles.replaysListHeaderSubtitle}>
              {t("session-replay.replays-count", {
          number: props.visibleRecordingsCount
        })}
            </div>}
        </div>}
    </div>;
}